import * as React from "react";
import { observable, action, runInAction, computed, IObservableArray } from "mobx";
import { observer, inject, Provider } from "mobx-react";
import { Link } from "../General/Link";
import { Urls } from "../Urls";

import { AppStore } from "../Stores/AppStore";
import * as DocumenManagement from "../DocumentManagement";
import { CollectionTreeViewStoreBase } from "../Stores/CollectionTreeViewStoreBase";
import { MoveTreeViewStore } from "../Stores/MoveTreeViewStore";
import { CollectionStoreImpl } from "../Stores/CollectionStore";
import { Strings } from "../Stores/StringsStore";


interface ICollectionTreeProps {
	inSelectionMode: boolean;
}

interface ICollectionTreeStoreProps {
	treeViewStore?: CollectionTreeViewStoreBase;
	testId?: string;
	testName?: string;
}


@observer
export class CollectionTree extends React.Component<ICollectionTreeStoreProps, {}> {

	store: CollectionTreeViewStoreBase;

	componentWillMount() {
		this.store = this.props.treeViewStore;
	}

	componentDidMount() {
		this.store.getRoots();
	}

	@computed get treeViewClass(): string {
		return this.store.inSelectionMode ? "treeview is--selection" : "treeview";
	}


	render() {
		return <Provider treeViewStore={this.store}>
			<div className={this.treeViewClass} data-test-id={this.props.testId} data-test-name={this.props.testName} >
				<CollectionTreeViewItemList collection={this.store.model} testName="rootCollections" />
			</div>
		</Provider>
	}

}

interface ICollectionTreeViewItemProps extends ICollectionTreeStoreProps {
	collection: DocumenManagement.CollectionInfo;
}


@observer
class CollectionTreeViewItemList extends React.Component<ICollectionTreeViewItemProps, {}>{

	render() {
		return this.props.collection.subCollections ? <div data-test-id={this.props.testId} data-test-name={this.props.testName}>
			{
				this.props.collection.subCollections.map(item => {
					return <CollectionTreeItem key={item.guid} collection={item} />;
				})
			}
		</div> : null;
	}
}


@inject("treeViewStore")
@observer
class CollectionTreeItem extends React.Component<ICollectionTreeViewItemProps, {}>{

	store: CollectionTreeViewStoreBase;

	componentWillMount() {
		this.store = this.props.treeViewStore;
	}

	private get moveStore(): MoveTreeViewStore {
		if (!(this.store instanceof MoveTreeViewStore)) return null;
		return this.store as MoveTreeViewStore;
	}

	@action handleClickItem = (e: React.MouseEvent<any>) => {
		this.store.currentFocusedCollection = this.props.collection.guid;
		//The treeview is in selction mode and a click adds an collection to the selectedCollections
		if (this.store.inSelectionMode) {
			this.store.addCollectionToSelection(this.props.collection);
			e.preventDefault();
		}
		else if (this.moveStore) {
			this.moveStore.setMoveDestination(this.props.collection);
			e.preventDefault();
		}
		else {
			//default behaviour for clicking is to open the collection details. 
			e.preventDefault();
			AppStore.keepOnPage(this.collectionLink, true, this.props.treeViewStore.collectionStore.areaUiInfo.selectCollapse);
		}
	}

	@action expandTreeNode = () => {
		this.props.collection.isExpanded = !this.props.collection.isExpanded;
		if (this.props.collection.isExpanded) {
			this.store.getSubCollection(this.props.collection);
		}
		else if (this.props.collection.subCollections) {
			this.store.popTreeNodeFromPath(this.props.collection.guid);
			this.store.pushCollectionTreePathHistory();
			this.props.collection.subCollections.clear();
		}
	}

	//****Computed Values *****////

	@computed get collectionLink() {
		return `${Urls.collections}/${this.props.collection.guid}?treepath=${this.store.treePath.filter(function (e) { return e }).join(",")}`;
	}

	@computed get isMultiSelected(): boolean {
		return this.store.selectedCollections.some(collection => collection.guid === this.props.collection.guid);
	}

	@computed get nodeIsSelectedItem(): boolean {
		return this.store.currentFocusedCollection === this.props.collection.guid;
	}

	//****Computed CSS****//

	@computed get nodeClass(): string {
		let className = "treeview__node";
		if (!this.props.collection.isExpanded) {
			className += " is--closed";
		}
		if (this.store.currentResult === this.props.collection.guid) {
			className += " currentResult";
		}
		return className;
	}

	@computed get nodeSelectedClass(): string {
		let finalClass = "treeview__label";
		if (this.store.inSelectionMode && this.isMultiSelected) {
			finalClass = "treeview__label is--selection-selected";
		}
		else if (this.nodeIsSelectedItem) {
			finalClass = "treeview__label is--item-open";
		}
		return finalClass;
	}

	@computed get buttonTooltip() {
		return this.props.collection.isExpanded ? Strings.general.closeNode : Strings.general.expandNode;
	}

	render(): JSX.Element {
		return <ul>
			<li className={this.nodeClass} data-test-name="collectionTreeItem" data-test-id={this.props.collection.name}>
				<span className={this.nodeSelectedClass}>
					<label>
						<input type="checkbox" checked={this.isMultiSelected} data-test-name="selectedCheckBox"></input>
						<Link onClick={this.handleClickItem} to={this.collectionLink} className="treeview__label-title">{this.props.collection.name}</Link>
						<span onClick={this.handleClickItem} className="treeview__label-check"></span>
					</label>
				</span>
				{this.props.collection.hasSubCollections ? <span id={this.props.collection.guid} className="treeview__toggle" onClick={this.expandTreeNode} data-test-name="childrenToggle" title={this.buttonTooltip}></span> : null}
				{this.props.collection.isExpanded ? <CollectionTreeViewItemList collection={this.props.collection} testName="childCollections" /> : null}
			</li>
		</ul>;
	}


}